import { useState } from 'react';
import { Permission, decodePermissions, has } from '@callzone/shared';
import {
  displayName,
  memberAccent,
  memberAvatarVersion,
  useActiveGuild,
  useAliases,
  useStore,
} from '../store';
import { voice } from '../lib/voice';
import { api } from '../lib/api';
import { gateway } from '../lib/gateway';
import { clearCredentials } from '../lib/platform';
import { Avatar } from './Avatar';
import {
  CloseIcon,
  LockIcon,
  LogOutIcon,
  MicIcon,
  MicOffIcon,
  PencilIcon,
  SettingsIcon,
  VideoIcon,
  VolumeIcon,
  VolumeOffIcon,
} from './icons';
import type { ModalKind } from './Workspace';

interface Props {
  onOpenModal: (modal: ModalKind) => void;
  /** Só existe no celular, onde a barra é uma gaveta sobreposta. */
  onClose?: () => void;
}

export function ChannelSidebar({ onOpenModal, onClose }: Props) {
  const guild = useActiveGuild();
  const activeChannelId = useStore((s) => s.activeChannelId);
  const selectChannel = useStore((s) => s.selectChannel);
  const voiceStates = useStore((s) => s.voiceStates);
  const voiceParticipants = useStore((s) => s.voiceParticipants);
  const session = useStore((s) => s.voice);
  const toast = useStore((s) => s.toast);
  /** Pessoa marcada para ser puxada: no toque isso vem do segurar o dedo. */
  const [arrastando, setArrastando] = useState<string | null>(null);
  const [alvoSoltar, setAlvoSoltar] = useState<string | null>(null);
  useAliases();

  if (!guild) return null;
  const permissions = decodePermissions(guild.permissions);
  const canManageChannels = has(permissions, Permission.MANAGE_CHANNELS);
  const podeMover = has(permissions, Permission.MOVE_MEMBERS);

  const textChannels = guild.channels.filter((c) => c.type === 'text');
  const voiceChannels = guild.channels.filter((c) => c.type === 'voice');

  async function enterVoice(channelId: string) {
    selectChannel(channelId);
    if (session.channelId === channelId) return;
    await voice.join(guild!.guild.id, channelId);
  }

  /**
   * Arrastar alguem para outro canal de voz. Vale só para quem pode mover
   * membros; para os demais o arrastar nem começa.
   */
  async function puxarPara(userId: string, channelId: string) {
    const estado = voiceStates[userId];
    if (!estado || estado.channelId === channelId) return;
    try {
      await api.moderateVoice(guild!.guild.id, userId, { moveToChannelId: channelId });
      toast(`${displayName(guild, userId)} foi para ${
        guild!.channels.find((c) => c.id === channelId)?.name ?? 'outro canal'
      }.`);
    } catch (error) {
      toast(error instanceof Error ? error.message : 'Nao consegui mover.', 'error');
    }
  }

  async function renameChannel(channelId: string, atual: string) {
    const name = window.prompt('Novo nome do canal:', atual);
    if (!name?.trim() || name.trim() === atual) return;
    try {
      await api.updateChannel(channelId, { name: name.trim() });
    } catch (error) {
      toast(error instanceof Error ? error.message : 'Nao consegui renomear.', 'error');
    }
  }

  return (
    <div className="sidebar">
      <div className="sidebar-header">
        <span>{guild.guild.name}</span>
        {has(permissions, Permission.MANAGE_GUILD) && (
          <button
            className="icon-btn"
            title="Renomear servidor"
            onClick={async () => {
              const name = window.prompt('Novo nome do servidor:', guild.guild.name);
              if (!name?.trim() || name.trim() === guild.guild.name) return;
              try {
                await api.updateGuild(guild.guild.id, { name: name.trim() });
              } catch (error) {
                toast(error instanceof Error ? error.message : 'Nao consegui renomear.', 'error');
              }
            }}
          >
            <PencilIcon />
          </button>
        )}
        {onClose && (
          <button
            className="icon-btn drawer-close"
            onClick={onClose}
            title="Fechar"
            aria-label="Fechar canais"
          >
            <CloseIcon />
          </button>
        )}
      </div>

      <div className="sidebar-scroll">
        <div className="channel-group">
          <div className="channel-group-title">
            <span>Canais de texto</span>
            {canManageChannels && (
              <button title="Criar canal" onClick={() => onOpenModal({ kind: 'channel', type: 'text' })}>
                +
              </button>
            )}
          </div>
          {textChannels.map((channel) => (
            <div key={channel.id} className="channel-row">
              <button
                className={`channel-item ${channel.id === activeChannelId ? 'active' : ''}`}
                onClick={() => selectChannel(channel.id)}
              >
                <span className="sigil">{channel.isPrivate ? <LockIcon /> : '#'}</span>
                <span className="name">{channel.name}</span>
              </button>
              {canManageChannels && (
                <button
                  className="channel-edit"
                  title="Renomear canal"
                  onClick={(e) => {
                    e.stopPropagation();
                    void renameChannel(channel.id, channel.name);
                  }}
                >
                  <PencilIcon />
                </button>
              )}
            </div>
          ))}
          {textChannels.length === 0 && (
            <div style={{ padding: '4px 8px', fontSize: 12, color: 'var(--text-dim)' }}>
              Nenhum canal de texto.
            </div>
          )}
        </div>

        <div className="channel-group">
          <div className="channel-group-title">
            <span>Canais de voz</span>
            {canManageChannels && (
              <button title="Criar canal" onClick={() => onOpenModal({ kind: 'channel', type: 'voice' })}>
                +
              </button>
            )}
          </div>
          {voiceChannels.map((channel) => {
            const occupants = Object.values(voiceStates).filter((v) => v.channelId === channel.id);
            const privado = channel.isPrivate;
            return (
              <div
                key={channel.id}
                className={`channel-row ${alvoSoltar === channel.id ? 'alvo-soltar' : ''}`}
                onDragOver={(e) => {
                  if (!arrastando) return;
                  e.preventDefault();
                  e.dataTransfer.dropEffect = 'move';
                  setAlvoSoltar(channel.id);
                }}
                onDragLeave={() => setAlvoSoltar((atual) => (atual === channel.id ? null : atual))}
                onDrop={(e) => {
                  e.preventDefault();
                  const userId = e.dataTransfer.getData('text/plain') || arrastando;
                  setAlvoSoltar(null);
                  setArrastando(null);
                  if (userId) void puxarPara(userId, channel.id);
                }}
              >
                <button
                  className={`channel-item ${channel.id === activeChannelId ? 'active' : ''}`}
                  onClick={() => {
                    // Com alguem marcado no toque, o clique no canal e o "soltar".
                    if (arrastando) {
                      const quem = arrastando;
                      setArrastando(null);
                      void puxarPara(quem, channel.id);
                      return;
                    }
                    void enterVoice(channel.id);
                  }}
                >
                  <span className="sigil">{privado ? <LockIcon /> : <VolumeIcon />}</span>
                  <span className="name">{channel.name}</span>
                  {channel.userLimit > 0 && (
                    <span className="count">
                      {occupants.length}/{channel.userLimit}
                    </span>
                  )}
                </button>
                {canManageChannels && (
                  <button
                    className="channel-edit"
                    title="Renomear canal"
                    onClick={(e) => {
                      e.stopPropagation();
                      void renameChannel(channel.id, channel.name);
                    }}
                  >
                    <PencilIcon />
                  </button>
                )}
                {occupants.length > 0 && (
                  <div className="voice-occupants">
                    {occupants.map((state) => {
                      const participant = voiceParticipants[state.userId];
                      return (
                        <div
                          key={state.userId}
                          className={`voice-occupant ${participant?.speaking ? 'speaking' : ''} ${
                            podeMover ? 'arrastavel' : ''
                          }`}
                          draggable={podeMover}
                          onDragStart={(e) => {
                            e.dataTransfer.setData('text/plain', state.userId);
                            e.dataTransfer.effectAllowed = 'move';
                            setArrastando(state.userId);
                          }}
                          onDragEnd={() => setArrastando(null)}
                          /*
                           * No toque nao existe arrastar nativo. Segurar o dedo
                           * marca a pessoa; o proximo toque num canal a leva
                           * para la — mesmo gesto em duas etapas.
                           */
                          onPointerDown={(e) => {
                            if (!podeMover || e.pointerType !== 'touch') return;
                            const timer = window.setTimeout(() => setArrastando(state.userId), 500);
                            const cancelar = () => {
                              window.clearTimeout(timer);
                              window.removeEventListener('pointerup', cancelar);
                              window.removeEventListener('pointercancel', cancelar);
                            };
                            window.addEventListener('pointerup', cancelar);
                            window.addEventListener('pointercancel', cancelar);
                          }}
                        >
                          <Avatar
                            name={displayName(guild, state.userId)}
                            color={memberAccent(guild, state.userId)}
                            size={20}
                            userId={state.userId}
                            avatarVersion={memberAvatarVersion(guild, state.userId)}
                          />
                          <span className="name">{displayName(guild, state.userId)}</span>
                          <span className="badges">
                            {state.streaming && <span className="badge-live">AO VIVO</span>}
                            {state.camera && (
                              <span title="Camera ligada">
                                <VideoIcon />
                              </span>
                            )}
                            {(state.selfMute || state.serverMute) && (
                              <span
                                title={
                                  state.serverMute
                                    ? 'Silenciado por um moderador'
                                    : 'Microfone desligado'
                                }
                                className={state.serverMute ? 'badge-server-mute' : ''}
                              >
                                <MicOffIcon />
                              </span>
                            )}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
          {voiceChannels.length === 0 && (
            <div style={{ padding: '4px 8px', fontSize: 12, color: 'var(--text-dim)' }}>
              Nenhum canal de voz.
            </div>
          )}
        </div>
      </div>

      {/*
        Sem este aviso o "segurar o dedo" seria invisivel: a pessoa marcaria
        alguem sem perceber e o proximo toque num canal a moveria do nada.
      */}
      {arrastando && (
        <div className="puxando-aviso">
          <span>
            Levando <strong>{displayName(guild, arrastando)}</strong> — toque num canal
          </span>
          <button className="btn ghost small" onClick={() => setArrastando(null)}>
            Cancelar
          </button>
        </div>
      )}

      <UserBar onOpenModal={onOpenModal} />
    </div>
  );
}

function UserBar({ onOpenModal }: Props) {
  const me = useStore((s) => s.me);
  const guild = useActiveGuild();
  const session = useStore((s) => s.voice);
  useAliases();

  if (!me) return null;

  const inVoice = session.status === 'connected' || session.status === 'reconnecting';

  /**
   * Mesma sequencia do botao em "Acesso e dispositivos": sair da call, revogar
   * o dispositivo, fechar o gateway e so entao limpar o estado local. Inverter
   * a ordem faria o gateway tentar reconectar com um token ja revogado.
   */
  async function sair() {
    if (!window.confirm('Sair da sua conta neste navegador?')) return;
    await voice.leave(true).catch(() => undefined);
    await api.logout().catch(() => undefined);
    gateway.disconnect();
    clearCredentials();
    useStore.getState().reset();
  }

  return (
    <div className="user-bar">
      {/* Clicar em si mesmo abre o perfil: e onde as pessoas procuram. */}
      <button
        className="user-bar-eu"
        title="Seu perfil"
        onClick={() => onOpenModal({ kind: 'perfil' })}
      >
        <Avatar
          name={me.nickname}
          color={me.accentColor}
          size={32}
          presence="online"
          userId={me.id}
          avatarVersion={me.avatarVersion}
        />
        <div className="who">
          <div className="name">{guild ? displayName(guild, me.id) : me.nickname}</div>
          <div className="status">{inVoice ? 'Em call' : 'Online'}</div>
        </div>
      </button>
      <button
        className={`icon-btn ${session.micEnabled ? '' : 'danger-on'}`}
        title={session.micEnabled ? 'Desligar microfone' : 'Ligar microfone'}
        onClick={() => void voice.toggleMic()}
        disabled={!inVoice}
      >
        {session.micEnabled ? <MicIcon /> : <MicOffIcon />}
      </button>
      <button
        className={`icon-btn ${session.deafened ? 'danger-on' : ''}`}
        title={session.deafened ? 'Voltar a ouvir' : 'Mudo'}
        onClick={() => void voice.toggleDeafen()}
        disabled={!inVoice}
      >
        {session.deafened ? <VolumeOffIcon /> : <VolumeIcon />}
      </button>
      <button
        className="icon-btn"
        title="Conta, senha e dispositivos"
        onClick={() => onOpenModal({ kind: 'devices' })}
      >
        <SettingsIcon />
      </button>
      <button className="icon-btn danger-hover" title="Sair da conta" onClick={() => void sair()}>
        <LogOutIcon />
      </button>
    </div>
  );
}
